import { useQuery } from '@apollo/client';
import { Download, FileX, Receipt, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CancelCfdiDialog } from './CancelCfdiDialog';
import { downloadBase64Pdf, downloadXml } from './download-helpers';
import { INVOICES_QUERY } from './invoicing.gql';
import { INVOICE_STATUS_VALUES } from './types';
import type { Invoice, InvoiceStatusFilter } from './types';

import { useBranchScope } from '@/modules/auth/useAuth';
import { TotemMark } from '@/shared/brand/TotemLogo';
import { INVOICE_STATUS_FROM_GQL, INVOICE_STATUS_TO_GQL } from '@/shared/graphql/enums';
import { formatCentsString } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';

interface InvoicesResponse {
  invoices: { items: Invoice[]; page: { total: number; has_more: boolean } };
}

const STATUS_TONE: Record<
  string,
  'default' | 'destructive' | 'outline' | 'success' | 'warning' | 'info' | 'muted'
> = {
  pending: 'muted',
  stamping: 'info',
  stamped: 'success',
  cancel_requested: 'warning',
  cancelled: 'muted',
  cancel_failed: 'destructive',
  failed: 'destructive',
};

export function InvoicingPage(): JSX.Element {
  const { t } = useTranslation();
  const [status_filter, setStatusFilter] = useState<InvoiceStatusFilter>('');
  const [cancel_target, setCancelTarget] = useState<Invoice | null>(null);
  const [error_text, setErrorText] = useState<string | null>(null);
  const branch_id = useBranchScope();

  const { data, refetch, loading } = useQuery<InvoicesResponse>(INVOICES_QUERY, {
    variables: {
      offset: 0,
      limit: 100,
      status: status_filter === '' ? null : INVOICE_STATUS_TO_GQL[status_filter],
      branch_id,
    },
    fetchPolicy: 'network-only',
    pollInterval: 60_000,
  });

  const items = (data?.invoices.items ?? []).map((invoice) => ({
    ...invoice,
    status:
      INVOICE_STATUS_FROM_GQL[invoice.status as keyof typeof INVOICE_STATUS_FROM_GQL] ??
      invoice.status,
  }));

  const onClickDownloadXml = async (invoice: Invoice): Promise<void> => {
    setErrorText(null);
    try {
      await downloadXml(invoice);
    } catch (err) {
      setErrorText(err instanceof Error ? err.message : String(err));
    }
  };

  const onClickDownloadPdf = async (invoice: Invoice): Promise<void> => {
    setErrorText(null);
    try {
      await downloadBase64Pdf(invoice);
    } catch (err) {
      setErrorText(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight">
            {t('invoicing.title')}
          </h2>
          <p className="text-sm text-muted-foreground">{t('invoicing.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="flex h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={status_filter}
            onChange={(e) => setStatusFilter(e.target.value as InvoiceStatusFilter)}
          >
            <option value="">{t('invoicing.filter_all_statuses')}</option>
            {INVOICE_STATUS_VALUES.map((s) => (
              <option key={s} value={s}>
                {t(`invoicing.status_${s}`)}
              </option>
            ))}
          </select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              void refetch();
            }}
            disabled={loading}
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
            <span>{t('common.refresh')}</span>
          </Button>
        </div>
      </header>

      {error_text ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error_text}
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {items.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 p-6 text-center text-sm text-muted-foreground">
              <TotemMark className="h-10 w-auto text-muted-foreground/40" aria-hidden />
              {t('invoicing.empty')}
            </CardContent>
          </Card>
        ) : null}
        {items.map((invoice) => (
          <Card key={invoice.id}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Receipt className="h-4 w-4" aria-hidden />
                  {invoice.series ? `${invoice.series}-` : ''}
                  {invoice.folio_number ?? '—'}
                </CardTitle>
                <Badge variant={STATUS_TONE[invoice.status] ?? 'outline'}>
                  {t(`invoicing.status_${invoice.status}`)}
                </Badge>
              </div>
              {invoice.uuid ? (
                <p className="font-mono text-xs text-muted-foreground">{invoice.uuid}</p>
              ) : null}
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('invoicing.field_receptor')}</span>
                <span className="text-right">
                  {invoice.legal_name_receptor}
                  <br />
                  <span className="font-mono text-xs text-muted-foreground">
                    {invoice.rfc_receptor}
                  </span>
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('invoicing.field_total')}</span>
                <span className="text-right font-mono tabular-nums">
                  {formatCentsString(invoice.total_cents)} {invoice.currency}
                </span>
              </div>
              {invoice.error_message ? (
                <div className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">
                  {invoice.error_message}
                </div>
              ) : null}
            </CardContent>
            <CardFooter className="justify-end gap-2">
              {invoice.uuid ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      void onClickDownloadXml(invoice);
                    }}
                  >
                    <Download className="h-4 w-4" aria-hidden />
                    <span>XML</span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      void onClickDownloadPdf(invoice);
                    }}
                  >
                    <Download className="h-4 w-4" aria-hidden />
                    <span>PDF</span>
                  </Button>
                </>
              ) : null}
              {invoice.status === 'stamped' || invoice.status === 'cancel_failed' ? (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => setCancelTarget(invoice)}
                >
                  <FileX className="h-4 w-4" aria-hidden />
                  <span>{t('invoicing.cancel_action')}</span>
                </Button>
              ) : null}
            </CardFooter>
          </Card>
        ))}
      </div>

      {cancel_target ? (
        <CancelCfdiDialog
          invoice={cancel_target}
          on_close={() => setCancelTarget(null)}
          on_done={async () => {
            setCancelTarget(null);
            await refetch();
          }}
          on_error={setErrorText}
        />
      ) : null}
    </div>
  );
}
